import {
  api,
  type components,
  formatTenne,
  formatTenneCurrency,
  parseDecimalInput,
} from "@awtoban/shared";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  toast,
  useUndoToast,
} from "@awtoban/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { LogOut, Minus, Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "../../i18n";
import { LocaleSwitcher } from "../../i18n/LocaleSwitcher";
import type { CommitPayload } from "../../offline/db";
import { deleteIfUnsynced, enqueue, getItem } from "../../offline/outbox";
import { postSync } from "../../offline/poster";
import { drainOutbox } from "../../offline/sync";
import { refreshPending } from "../../offline/syncManager";
import { useAuth } from "../auth/AuthProvider";
import { emptyForm, type OperatorFormValues } from "./form-types";
import { EndOfShiftMenu, ReprintMenu } from "./HeaderMenu";
import { LineItemsPanel } from "./LineItemsPanel";
import { ReconcileDialog } from "./ReconcileDialog";
import { buildReceiptHtml, printHtml } from "./receipt";
import { SyncStatus } from "./SyncStatus";
import { type CompanyHit, useCompanyLookup } from "./useCompanyLookup";
import { usePreview } from "./usePreview";
import { useVehicleHistory } from "./useVehicleHistory";

function bcp47(locale: Locale): string {
  if (locale === "en") return "en-US";
  if (locale === "ru") return "ru-RU";
  return "tk-TM";
}

function cityName(c: City, locale: Locale): string {
  switch (locale) {
    case "ru":
      return c.name_ru || c.name_tk;
    case "en":
      return c.name_en || c.name_tk;
    default:
      return c.name_tk;
  }
}

type City = components["schemas"]["City"];

const UUID_FACTORY = () => crypto.randomUUID();
const UNDO_WINDOW_SECONDS = 5;

// Phase 5 will split this into a presentational shell + a couple of orchestrating
// hooks; for now it's the natural orchestration point for the whole flow.
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: see comment above
export function OperatorPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const auth = useAuth();
  const showUndo = useUndoToast();
  const [values, setValues] = React.useState<OperatorFormValues>(emptyForm);
  const [reconcileOpen, setReconcileOpen] = React.useState(false);
  const [chargeToAccount, setChargeToAccount] = React.useState(false);
  // Bumped after each commit to force-remount the city Selects; a Radix Select
  // whose `value` goes back to "" otherwise keeps showing its last label.
  const [resetNonce, setResetNonce] = React.useState(0);
  const plateRef = React.useRef<HTMLInputElement>(null);
  const formColRef = React.useRef<HTMLDivElement>(null);
  // The last truck shown a price but not yet issued — used to auto-record a
  // non-payment when the operator moves on to another truck or clears (an
  // operator only issues when they collect, and won't click anything otherwise).
  const pendingRef = React.useRef<{ plate: string; total: number } | null>(null);

  const update = React.useCallback(
    <K extends keyof OperatorFormValues>(key: K, val: OperatorFormValues[K]) =>
      setValues((v) => ({ ...v, [key]: val })),
    [],
  );

  const citiesQuery = useQuery({
    queryKey: ["cities"],
    queryFn: async () => {
      const { data } = await api.GET("/api/v1/cities/", {});
      return (data ?? []) as City[];
    },
    staleTime: 5 * 60_000,
  });
  const cities = citiesQuery.data ?? [];

  // Default `from` to the operator's home city the first time cities load.
  React.useEffect(() => {
    if (values.fromCityId === "" && auth.user?.station?.home_city?.id) {
      update("fromCityId", auth.user.station.home_city.id);
    }
  }, [auth.user, values.fromCityId, update]);

  // Prepaid company account linked to this plate (null = cash truck).
  const company = useCompanyLookup(values.vehiclePlate);
  const accountAvailable = company?.status === "active";
  // Drop "charge to account" whenever the matched account goes away (plate
  // changed/cleared) or is suspended — never charge a stale/invalid account.
  React.useEffect(() => {
    if (!accountAvailable && chargeToAccount) setChargeToAccount(false);
  }, [accountAvailable, chargeToAccount]);

  // Vehicle history (F2 to accept prefill)
  const history = useVehicleHistory(values.vehiclePlate);
  const showPrefillHint =
    history !== null && history.plate.replace(/\s/g, "") !== values.vehiclePlate.replace(/\s/g, "");

  function acceptPrefill() {
    if (!history) return;
    setValues((v) => {
      const next: OperatorFormValues = {
        ...v,
        vehiclePlate: history.plate,
        ownerName: history.owner_name || v.ownerName,
      };
      if (history.last_transaction) {
        next.totalAxles = String(history.last_transaction.total_axles);
        next.frontAxleTn = history.last_transaction.front_axle_tn;
      }
      return next;
    });
  }

  // Live preview. `keepPreviousData` in usePreview avoids flicker while the
  // debounced query refetches — but it also retains the last quote after the
  // form is cleared on commit. Gate everything on the *current* form so a reset
  // form shows an empty quote and a disabled Issue button, never stale numbers.
  const preview = usePreview(values);
  const formReady = Boolean(
    values.fromCityId &&
      values.toCityId &&
      values.fromCityId !== values.toCityId &&
      values.totalMassTn &&
      values.frontAxleTn &&
      values.totalAxles,
  );
  const quote = formReady && preview.data?.kind === "ok" ? preview.data.quote : undefined;
  // The account this commit will charge, if the operator opted in (null = cash).
  const chargeCompanyId = chargeToAccount && accountAvailable && company ? company.id : null;
  // Prepaid settlement split — display only; the server recomputes it under lock.
  const accountPortion =
    chargeCompanyId && company && quote ? Math.min(company.balance_tenne, quote.total_tenne) : 0;
  const cashDue = quote ? quote.total_tenne - accountPortion : 0;
  // Permit is engine-derived, not an operator toggle; show it read-only.
  const permitApplies = Boolean(
    quote?.line_items?.some((li) => li.kind === "permit" && li.amount_tenne > 0),
  );
  const previewStatus: React.ComponentProps<typeof LineItemsPanel>["status"] = !formReady
    ? "idle"
    : preview.isFetching
      ? "loading"
      : preview.isError
        ? "error"
        : preview.data?.kind === "not_provisioned"
          ? "not_provisioned"
          : preview.data?.kind === "distance_missing"
            ? "distance_missing"
            : quote
              ? "ready"
              : "idle";

  // Void an already-server-persisted tx (account commit, or a cash sale that
  // already drained) through the lifecycle path.
  const serverVoid = React.useCallback(
    async (txId: string, invoiceNumber?: string) => {
      const { error } = await api.POST("/api/v1/operator/transactions/{tx_uuid}/void", {
        params: { path: { tx_uuid: txId } },
        body: { reason: "operator_undo" } as never,
      });
      if (error) {
        toast.error(t("issue.undo_failed"));
        return;
      }
      toast.success(t("issue.undone", { number: invoiceNumber ?? "" }));
    },
    [t],
  );

  // Undo at click time reads the outbox entry's CURRENT state (§8.7): an item
  // still pending (not yet drained) is dropped locally — nothing reached the
  // server, no invoice number consumed; a synced item goes through serverVoid.
  const outboxUndo = React.useCallback(
    async (txUuid: string, invoiceNumber?: string) => {
      const removed = await deleteIfUnsynced(txUuid);
      if (removed) {
        toast.success(t("issue.undone_local"));
        return;
      }
      await serverVoid(txUuid, invoiceNumber);
    },
    [serverVoid, t],
  );

  // Render + print the driver's booth receipt, client-side, so it works OFFLINE
  // (WeasyPrint is server-only). Shows the official invoice number when synced,
  // else a clearly-marked PROVISIONAL receipt with a PENDING reference (§8.10).
  const printBoothReceipt = React.useCallback(
    (
      vars: { values: OperatorFormValues; uuid: string; quote: components["schemas"]["Quote"] },
      invoiceNumber?: string,
    ) => {
      const v = vars.values;
      const tag = bcp47(locale);
      const from = cities.find((c) => c.id === Number(v.fromCityId));
      const to = cities.find((c) => c.id === Number(v.toCityId));
      const lines = (vars.quote.line_items ?? [])
        .filter((li) => li.amount_tenne > 0)
        .map((li) => ({
          label: t(`quote.${li.kind}`),
          amount: formatTenne(li.amount_tenne, tag),
        }));
      printHtml(
        buildReceiptHtml({
          title: t("receipt.title"),
          stationName: auth.user?.station?.name ?? "—",
          stationCode: auth.user?.station?.code ?? "—",
          operatorLabel: t("header.operator"),
          operator: auth.user?.username ?? "—",
          dateLabel: t("receipt.date"),
          dateText: new Date().toLocaleString(tag),
          vehicleLabel: t("form.vehicle"),
          plate: v.vehiclePlate.trim(),
          owner: v.ownerName.trim() || undefined,
          routeLabel: t("form.route"),
          route: from && to ? `${cityName(from, locale)} → ${cityName(to, locale)}` : "—",
          lines,
          totalLabel: t("quote.total"),
          totalText: `${formatTenne(vars.quote.total_tenne, tag)} TMT`,
          referenceLabel: invoiceNumber ? t("receipt.invoice_no") : t("receipt.reference"),
          referenceValue: invoiceNumber ?? `PENDING-${vars.uuid.slice(0, 8).toUpperCase()}`,
          provisionalNote: invoiceNumber ? undefined : t("receipt.provisional_note"),
        }),
      );
    },
    [auth.user, cities, locale, t],
  );

  const commit = useMutation({
    mutationFn: async (payload: {
      values: OperatorFormValues;
      uuid: string;
      companyId: string | null;
      quote: components["schemas"]["Quote"];
    }) => {
      const v = payload.values;

      // Account settlement needs the live prepaid balance read under lock —
      // online only (cash-only-offline is server-enforced). Direct commit.
      if (payload.companyId) {
        const { data, error } = await api.POST("/api/v1/transactions/", {
          body: {
            tx_uuid: payload.uuid,
            from_city_id: Number(v.fromCityId),
            to_city_id: Number(v.toCityId),
            total_mass_tn: parseDecimalInput(v.totalMassTn) ?? v.totalMassTn,
            front_axle_tn: parseDecimalInput(v.frontAxleTn) ?? v.frontAxleTn,
            total_axles: Number(v.totalAxles),
            is_oversized: v.isOversized,
            vehicle_plate: v.vehiclePlate.trim(),
            owner_name: v.ownerName.trim(),
            settle: true,
            company_account_id: payload.companyId,
          } as never,
        });
        if (error || !data) throw new Error("commit failed");
        const tx = data as components["schemas"]["TollTransaction"];
        return {
          mode: "server" as const,
          txId: tx.id,
          invoiceNumber: tx.invoice_number,
          total: Number(tx.total_tenne),
        };
      }

      // Cash: LOCAL-FIRST. Write to the durable outbox BEFORE any network, then
      // drain. Online → drains instantly with the official number; offline →
      // stays pending and syncs on reconnect. The sale is never lost.
      const capturedAt = new Date().toISOString();
      const distance = payload.quote.line_items?.[0]?.distance_km ?? "0";
      const cp: CommitPayload = {
        tx_uuid: payload.uuid,
        from_city_id: Number(v.fromCityId),
        to_city_id: Number(v.toCityId),
        total_mass_tn: parseDecimalInput(v.totalMassTn) ?? v.totalMassTn,
        front_axle_tn: parseDecimalInput(v.frontAxleTn) ?? v.frontAxleTn,
        total_axles: Number(v.totalAxles),
        is_oversized: v.isOversized,
        vehicle_plate: v.vehiclePlate.trim(),
        owner_name: v.ownerName.trim(),
        submitted_distance_km: distance,
        captured_at: capturedAt,
        offline_captured: true,
        client_total_tenne: payload.quote.total_tenne,
      };
      await enqueue(cp, capturedAt);
      let invoiceNumber: string | undefined;
      try {
        await drainOutbox(postSync);
        const item = await getItem(payload.uuid);
        if (item?.status === "synced") {
          invoiceNumber = item.invoice_number;
        }
      } catch {
        // Offline — the item stays pending and drains on the next trigger.
      }
      return {
        mode: "outbox" as const,
        txId: payload.uuid,
        invoiceNumber,
        total: payload.quote.total_tenne,
      };
    },
    onSuccess: (res, variables) => {
      // Settled (or queued) — this truck is no longer a pending non-payment.
      pendingRef.current = null;
      const expires = new Date(Date.now() + UNDO_WINDOW_SECONDS * 1000);
      const undoLabel = (seconds: number) => t("issue.undo", { seconds });
      const onUndo =
        res.mode === "server"
          ? () => serverVoid(res.txId, res.invoiceNumber)
          : () => outboxUndo(res.txId, res.invoiceNumber);

      if (res.invoiceNumber) {
        // Official numbered receipt (account commit, or cash drained online).
        showUndo({
          title: t("issue.success_title", { number: res.invoiceNumber }),
          description: t("issue.success_description", {
            amount: formatTenne(Number(res.total), bcp47(locale)),
          }),
          expiresAt: expires,
          undoLabel,
          onUndo,
        });
      } else {
        // Provisional (offline): saved locally; the official number arrives
        // when it syncs — the provisional receipt prints now.
        showUndo({
          title: t("issue.saved_offline_title"),
          description: t("issue.saved_offline_description", {
            amount: formatTenne(Number(res.total), bcp47(locale)),
          }),
          expiresAt: expires,
          undoLabel,
          onUndo,
        });
      }

      // Print the booth receipt for the driver — works ONLINE and OFFLINE
      // (client-rendered). Official invoice number if synced, else a clearly
      // marked PROVISIONAL receipt with a PENDING reference.
      printBoothReceipt(variables, res.invoiceNumber);

      setValues((v) => ({
        ...emptyForm,
        fromCityId: v.fromCityId, // remember default
      }));
      setChargeToAccount(false);
      setResetNonce((n) => n + 1);
      plateRef.current?.focus();
      // Reflect the new outbox depth in the header indicator immediately.
      void refreshPending();
    },
    onError: () => {
      toast.error(t("issue.error_title"), {
        description: t("issue.error_description"),
      });
    },
  });

  const recordAbandon = React.useCallback((p: { plate: string; total: number }) => {
    void api.POST("/api/v1/operator/transactions/refuse", {
      body: { plate: p.plate, total_tenne: p.total, reason: "not_issued" } as never,
    });
  }, []);

  // Auto-record a non-payment: an operator only issues when they collect, so a
  // priced truck superseded by the next truck (or cleared away) was turned away
  // unpaid. Fire-and-forget — the integrity trail needs it without a click.
  React.useEffect(() => {
    const plate = values.vehiclePlate.trim().replace(/\s+/g, "").toUpperCase();
    const prev = pendingRef.current;
    if (quote && plate) {
      if (prev && prev.plate !== plate) recordAbandon(prev);
      pendingRef.current = { plate, total: quote.total_tenne };
    } else if (!plate && prev) {
      recordAbandon(prev);
      pendingRef.current = null;
    }
  }, [quote, values.vehiclePlate, recordAbandon]);

  // F9 issues; F2 accepts the autofill hint; Enter advances focus.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "F9") {
      e.preventDefault();
      attemptIssue();
      return;
    }
    if (e.key === "F2" && showPrefillHint) {
      e.preventDefault();
      acceptPrefill();
      return;
    }
    // Suppress implicit submit on Enter; advance focus instead. Scope the
    // traversal to the left input column so Enter never lands on the right-hand
    // Issue button (F9 is the only way to commit).
    if (
      e.key === "Enter" &&
      e.target instanceof HTMLElement &&
      e.target.tagName === "INPUT" &&
      formColRef.current?.contains(e.target)
    ) {
      e.preventDefault();
      const focusables = Array.from(
        formColRef.current.querySelectorAll<HTMLElement>(
          'input, button, [role="combobox"], [role="switch"]',
        ),
      ).filter((el) => !el.hasAttribute("disabled"));
      const idx = focusables.indexOf(e.target as HTMLElement);
      const next = focusables[idx + 1] ?? focusables[0];
      next?.focus();
    }
  };

  function perAxleSum(): number {
    return values.axleReadings.reduce((acc, r) => acc + (Number(r.loadTn) || 0), 0);
  }

  function attemptIssue() {
    if (!quote || preview.data?.kind === "distance_missing") {
      toast.error(t("issue.not_ready"));
      return;
    }
    if (quote.total_tenne <= 0) {
      toast.error(t("issue.nothing_to_charge"));
      return;
    }
    if (commit.isPending) return;

    if (values.perAxleEnabled && values.axleReadings.length > 0 && values.totalMassTn) {
      // Compare in integer centitonnes so 11.50 vs 11.5 never reconciles falsely.
      const sumCt = Math.round(perAxleSum() * 100);
      const typedCt = Math.round(Number(parseDecimalInput(values.totalMassTn) ?? "0") * 100);
      if (sumCt !== typedCt) {
        setReconcileOpen(true);
        return;
      }
    }
    commit.mutate({ values, uuid: UUID_FACTORY(), companyId: chargeCompanyId, quote });
  }

  return (
    <div
      onKeyDown={onKeyDown}
      role="application"
      className="flex min-h-screen flex-col bg-paper-soft"
    >
      <header className="flex flex-wrap items-center gap-x-6 gap-y-2 border-line-strong border-b bg-paper px-4 py-3 sm:px-6">
        <div>
          <div className="text-[11px] text-ink-muted uppercase tracking-wider">
            {t("header.station")}
          </div>
          <div className="font-semibold">
            {auth.user?.station?.name ?? "—"}{" "}
            <span className="text-ink-muted text-sm">({auth.user?.station?.code ?? "—"})</span>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-4 text-sm sm:gap-6">
          <SyncStatus />
          <div>
            <div className="text-[11px] text-ink-muted uppercase tracking-wider">
              {t("header.operator")}
            </div>
            <div className="font-medium">{auth.user?.username}</div>
          </div>
          <ReprintMenu />
          <EndOfShiftMenu />
          <LocaleSwitcher />
          <Button variant="ghost" size="sm" onClick={() => void auth.endShift()}>
            <LogOut className="h-4 w-4" />
            {t("shift.end")}
          </Button>
        </div>
      </header>

      {/* Two-column body — stacks to one column below lg (tablet portrait). */}
      <main className="mx-auto grid w-full max-w-[1400px] flex-1 grid-cols-1 gap-6 px-4 py-4 lg:grid-cols-[1fr_minmax(340px,420px)] lg:px-6 lg:py-6">
        <div ref={formColRef} className="space-y-6">
          <Section title={t("form.vehicle")}>
            <Field label={t("form.plate")} htmlFor="plate">
              <Input
                id="plate"
                ref={plateRef}
                inputSize="lg"
                autoFocus
                placeholder="AG 1234 AH"
                value={values.vehiclePlate}
                onChange={(e) => update("vehiclePlate", e.target.value.toUpperCase())}
                className="font-medium tracking-wider"
              />
              {showPrefillHint && history ? (
                <p className="mt-1.5 text-[12px] text-ink-muted">
                  <span className="font-medium text-ink">{history.plate}</span>
                  {history.owner_name ? ` — ${history.owner_name}` : null}
                  {" · "}
                  <button
                    type="button"
                    onClick={acceptPrefill}
                    className="underline decoration-dotted hover:text-ink"
                  >
                    F2 to autofill
                  </button>
                </p>
              ) : null}
            </Field>
            <Field label={t("form.owner")}>
              <Input
                value={values.ownerName}
                onChange={(e) => update("ownerName", e.target.value)}
                placeholder={t("form.owner_optional")}
              />
            </Field>
            {company ? (
              <CompanyBanner
                company={company}
                locale={locale}
                chargeToAccount={chargeToAccount}
                onToggle={setChargeToAccount}
              />
            ) : null}
          </Section>

          <Section title={t("form.route")}>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("form.from")}>
                <Select
                  {...(values.fromCityId !== "" ? { value: String(values.fromCityId) } : {})}
                  onValueChange={(v) => update("fromCityId", Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("form.select_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {cityName(c, locale)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("form.to")}>
                <Select
                  key={`to-${resetNonce}`}
                  {...(values.toCityId !== "" ? { value: String(values.toCityId) } : {})}
                  onValueChange={(v) => update("toCityId", Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("form.select_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {cities
                      .filter((c) => c.id !== values.fromCityId)
                      .map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {cityName(c, locale)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          <Section title={t("form.weights")}>
            <div className="grid grid-cols-3 gap-4">
              <Field label={t("form.total_mass")}>
                <Input
                  inputMode="decimal"
                  inputSize="lg"
                  value={values.totalMassTn}
                  onChange={(e) => update("totalMassTn", e.target.value)}
                />
              </Field>
              <Field label={t("form.front_axle")}>
                <Input
                  inputMode="decimal"
                  inputSize="lg"
                  value={values.frontAxleTn}
                  onChange={(e) => update("frontAxleTn", e.target.value)}
                />
              </Field>
              <Field label={t("form.axle_count")}>
                <Input
                  inputMode="numeric"
                  inputSize="lg"
                  value={values.totalAxles}
                  onChange={(e) => update("totalAxles", e.target.value.replace(/\D/g, ""))}
                />
              </Field>
            </div>

            <PerAxleDisclosure values={values} update={update} />
          </Section>

          <Section title={t("form.flags")}>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <FlagSwitch
                label={t("form.oversize")}
                checked={values.isOversized}
                onChange={(b) => update("isOversized", b)}
              />
              <div className="flex select-none items-center gap-3 opacity-70">
                <Switch checked={permitApplies} disabled />
                <span className="text-[15px]">{t("form.permit_required")}</span>
              </div>
            </div>
          </Section>
        </div>

        {/* Right column: quote (sticky alongside the form on wide screens). */}
        <aside className="flex min-h-[320px] flex-col self-start rounded-md border border-line-strong bg-paper lg:sticky lg:top-6 lg:min-h-[400px]">
          <div className="flex-1">
            <LineItemsPanel quote={quote} status={previewStatus} />
          </div>
          <div className="border-line-strong border-t p-6">
            {chargeToAccount && accountAvailable && company && quote ? (
              <div className="mb-4 space-y-1 rounded-md border border-line bg-paper-soft p-3 text-[13px]">
                <div className="flex justify-between text-ink-muted">
                  <span>{t("account.from_account")}</span>
                  <span className="tabular">{formatTenne(accountPortion, bcp47(locale))}</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span>{t("account.cash_due")}</span>
                  <span className="tabular">{formatTenne(cashDue, bcp47(locale))}</span>
                </div>
              </div>
            ) : null}
            <Button
              size="xl"
              className="w-full"
              onClick={attemptIssue}
              disabled={
                !quote ||
                quote.total_tenne <= 0 ||
                preview.data?.kind === "distance_missing" ||
                commit.isPending
              }
            >
              {commit.isPending
                ? t("issue.issuing")
                : chargeToAccount && accountAvailable
                  ? t("account.charge_and_issue")
                  : t("issue.button")}
            </Button>
            <p className="mt-2 text-center text-[11px] text-ink-subtle">
              {t("issue.shortcuts_hint")}
            </p>
          </div>
        </aside>
      </main>

      <ReconcileDialog
        open={reconcileOpen}
        typedTotalTn={values.totalMassTn || "0"}
        perAxleSumTn={perAxleSum().toFixed(2)}
        onEdit={() => setReconcileOpen(false)}
        onUsePerAxle={() => {
          const sum = perAxleSum().toFixed(2);
          setValues((v) => ({ ...v, totalMassTn: sum }));
          setReconcileOpen(false);
        }}
      />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 border-line border-b pb-1 font-medium text-ink-muted text-xs uppercase tracking-wider">
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

function CompanyBanner({
  company,
  locale,
  chargeToAccount,
  onToggle,
}: {
  company: CompanyHit;
  locale: Locale;
  chargeToAccount: boolean;
  onToggle: (b: boolean) => void;
}) {
  const { t } = useTranslation();
  if (company.status === "suspended") {
    return (
      <div className="rounded-md border border-warning bg-warning/10 p-3 text-[13px] text-warning-fg">
        <span className="font-medium">{company.name}</span> — {t("account.suspended")}
      </div>
    );
  }
  return (
    <div className="rounded-md border border-accent bg-accent-soft p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-medium text-accent text-sm">🏢 {company.name}</div>
          <div className="tabular text-[12px] text-ink-muted">
            {t("account.balance")}: {formatTenneCurrency(company.balance_tenne, bcp47(locale))}
          </div>
        </div>
        <div className="flex shrink-0 select-none items-center gap-2">
          <label htmlFor="charge-account" className="cursor-pointer text-[13px]">
            {t("account.charge")}
          </label>
          <Switch id="charge-account" checked={chargeToAccount} onCheckedChange={onToggle} />
        </div>
      </div>
    </div>
  );
}

let _flagSwitchSeq = 0;

function FlagSwitch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (b: boolean) => void;
}) {
  const id = React.useMemo(() => `flag-switch-${++_flagSwitchSeq}`, []);
  return (
    <div className="flex select-none items-center gap-3">
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
      <label htmlFor={id} className="cursor-pointer text-[15px]">
        {label}
      </label>
    </div>
  );
}

function PerAxleDisclosure({
  values,
  update,
}: {
  values: OperatorFormValues;
  update: <K extends keyof OperatorFormValues>(k: K, v: OperatorFormValues[K]) => void;
}) {
  const { t } = useTranslation();
  const toggle = () => {
    if (!values.perAxleEnabled) {
      const n = Number(values.totalAxles) || 0;
      const readings = Array.from({ length: n }, (_, i) => ({
        position: i + 1,
        loadTn: "",
      }));
      update("perAxleEnabled", true);
      update("axleReadings", readings);
    } else {
      update("perAxleEnabled", false);
      update("axleReadings", []);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={values.perAxleEnabled}
        className="inline-flex items-center gap-1 text-[13px] text-accent hover:underline"
      >
        {values.perAxleEnabled ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
        {values.perAxleEnabled ? t("form.per_axle_hide") : t("form.per_axle_enter")}
      </button>

      {values.perAxleEnabled ? (
        <div className="mt-3 space-y-2">
          {values.axleReadings.map((r, idx) => (
            <div key={r.position} className="grid grid-cols-[60px_1fr_auto] items-center gap-3">
              <span className="text-ink-muted text-xs uppercase tracking-wider">
                {t("form.axle_n", { n: r.position })}
              </span>
              <Input
                inputMode="decimal"
                value={r.loadTn}
                onChange={(e) => {
                  const next = values.axleReadings.slice();
                  next[idx] = { ...r, loadTn: e.target.value };
                  update("axleReadings", next);
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const next = values.axleReadings
                    .filter((_, i) => i !== idx)
                    .map((x, i) => ({ ...x, position: i + 1 }));
                  update("axleReadings", next);
                }}
                className="text-ink-subtle hover:text-danger"
                aria-label={t("form.remove_axle", { n: r.position })}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
